import type { PropertyResponse } from '@/shared/api';
import { NumberStepper } from '@/shared/components';
import type { GuestFormValues } from './manualBookingRules';
import { fieldInput, ManualBookingStep, ManualField } from './ManualBookingStep';

interface Props {
  /** Ohne festes Apartment (Einstieg aus der Liste) wird es hier gewählt. */
  choices: PropertyResponse[] | null;
  propertyId: string;
  apartmentName: string;
  onPropertyChange: (id: string) => void;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  guest: GuestFormValues;
  onGuestChange: <K extends keyof GuestFormValues>(field: K, value: GuestFormValues[K]) => void;
}

const COUNTS: Array<
  [keyof Pick<GuestFormValues, 'adults' | 'children' | 'infants'>, string, number]
> = [
  ['adults', 'Erwachsene', 1],
  ['children', 'Kinder', 0],
  ['infants', 'Kleinkinder', 0],
];

/** Schritt 1: Apartment, Anreise und Abreise, Personen. */
export function StayStep(props: Props) {
  return (
    <ManualBookingStep
      n={1}
      title="Aufenthalt"
      hint="Apartment und Zeitraum"
      note="Kinder zählen für die Kurtaxe, Kleinkinder nicht."
    >
      {props.choices ? (
        <ManualField label="Apartment *">
          <select
            value={props.propertyId}
            onChange={(event) => props.onPropertyChange(event.target.value)}
            className={fieldInput}
            aria-label="Apartment"
          >
            <option value="">Apartment wählen</option>
            {props.choices.map((property) => (
              <option key={property.id} value={property.id}>
                {property.adminDisplayName ?? property.name}
              </option>
            ))}
          </select>
        </ManualField>
      ) : (
        <ManualField label="Apartment">
          <span className={fieldInput}>{props.apartmentName}</span>
        </ManualField>
      )}
      <div className="grid grid-cols-2 gap-2.5">
        <ManualField label="Anreise">
          <input
            type="date"
            value={props.from}
            onChange={(e) => props.onFromChange(e.target.value)}
            className={fieldInput}
            aria-label="Anreise"
          />
        </ManualField>
        <ManualField label="Abreise">
          <input
            type="date"
            value={props.to}
            onChange={(e) => props.onToChange(e.target.value)}
            className={fieldInput}
            aria-label="Abreise"
          />
        </ManualField>
      </div>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {COUNTS.map(([field, label, min]) => (
          <div key={field} className="flex min-w-0 flex-col gap-1">
            <span className="text-ink-soft text-xs font-semibold">{label}</span>
            <NumberStepper
              aria-label={label}
              value={props.guest[field]}
              min={min}
              onChange={(next) => props.onGuestChange(field, next)}
            />
          </div>
        ))}
      </div>
    </ManualBookingStep>
  );
}
